import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ViewAsStrip, NotForYou } from './components/ViewAs';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Nav       from './Nav';
import RecordDock from './measuring/RecordDock';
import Login     from './pages/Login';
import Machines  from './pages/Machines';
import Moulds    from './pages/Moulds';
import Materials from './pages/Materials';
import Today     from './pages/Today';
import Parameters    from './pages/Parameters';
import Optimisation  from './pages/Optimisation';
import Parts         from './pages/Parts';
import AdminParts, { PartDetail } from './pages/AdminParts';
import AdminPartsImport from './pages/AdminPartsImport';
import AdminCatalogImport from './pages/AdminCatalogImport';
import AdminUsers, { UserDetail } from './pages/AdminUsers';
import AdminSuggestions from './pages/AdminSuggestions';
import StockSwitches from './pages/StockSwitches';
import StockDispatch from './pages/StockDispatch';
import Truck from './pages/Truck';
import Account   from './pages/Account';
import Activate  from './pages/Activate';
import Admin     from './pages/Admin';
import Schedule  from './pages/Schedule';
import FAQ       from './pages/FAQ';
import Orders    from './pages/Orders';
import Preview   from './pages/Preview';
import Insights   from './pages/Insights';
import Financials from './pages/Financials';
import Dashboard  from './pages/Dashboard';
import Closures      from './pages/Closures';
import Staffing      from './pages/Staffing';
import Customers     from './pages/Customers';
import Invoices      from './pages/Invoices';
import InvoiceForm   from './pages/InvoiceForm';
import WeightCalculator from './pages/WeightCalculator';
import Packaging from './pages/Packaging';
import Stock     from './pages/Stock';
import StockItem from './pages/StockItem';
import BuildSteps from './pages/BuildSteps';
import BulkTool      from './pages/BulkTool';
import History      from './pages/History';
import HistoryBoard from './pages/HistoryBoard';
import Measuring    from './pages/Measuring';
import JobsArchive     from './pages/JobsArchive';
import RecipeChanges  from './pages/RecipeChanges';
import Equipment      from './pages/Equipment';
import Settings      from './pages/Settings';
import DataTransfer  from './pages/DataTransfer';

// One address per page, and the role decides what is on it (the director,
// 3 Nov 2026). The old Admin addresses still work: bell notices and bookmarks use them.
function Keep({ to }) {
  const { search } = useLocation();
  return <Navigate to={to + search} replace />;
}
function Need({ what, children }) {
  const { can } = useAuth();
  return can(what) ? children : <NotForYou />;
}
function OrdersByRole() {
  const { can } = useAuth();
  return <Orders readOnly={!can('manage')} />;
}
function PartsByRole() {
  const { can } = useAuth();
  return can('manage') ? <AdminParts /> : <Parts />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f1117', color: '#6b7280', fontFamily: 'system-ui, sans-serif' }}>
      Loading...
    </div>
  );

  // Activation page is public — no login required
  if (window.location.pathname.startsWith('/activate')) {
    return <Routes><Route path="/activate/:token" element={<Activate />} /></Routes>;
  }

  // Preview page is public — no login required
  if (window.location.pathname.startsWith('/preview')) {
    return <Routes><Route path="/preview" element={<Preview />} /></Routes>;
  }

  if (!user) return <Login />;

  return (
    <div style={{ background: '#0f1117', minHeight: '100vh' }}>
      <Nav />
      <ViewAsStrip />
      <RecordDock />
      <Routes>
        <Route path="/"                    element={<Navigate to="/today" />} />
        <Route path="/today"               element={<Today />} />
        <Route path="/schedule"            element={<Need what="see_board"><Schedule /></Need>} />
        <Route path="/parts"               element={<PartsByRole />} />
        <Route path="/process"                    element={<Parameters />} />
        <Route path="/process/optimise/:id"       element={<Optimisation />} />
        <Route path="/admin"               element={<Admin />} />
        <Route path="/admin/machines"        element={<Machines />} />
        <Route path="/admin/machines/import" element={<Need what="manage"><AdminCatalogImport type="machine" /></Need>} />
        <Route path="/admin/moulds"          element={<Moulds />} />
        <Route path="/admin/moulds/import"   element={<Need what="manage"><AdminCatalogImport type="mould" /></Need>} />
        <Route path="/admin/materials"     element={<Materials />} />
        <Route path="/admin/parts"         element={<Keep to="/parts" />} />
        <Route path="/admin/import"          element={<Need what="manage"><AdminPartsImport /></Need>} />
        <Route path="/admin/parts/import"  element={<Navigate to="/admin/import" />} />
        <Route path="/admin/parts/:id"     element={<Need what="manage"><PartDetail /></Need>} />
        <Route path="/admin/schedule"      element={<Keep to="/schedule" />} />
        <Route path="/admin/closures"      element={<Closures />} />
        <Route path="/admin/staffing"      element={<Need what="see_staffing"><Staffing /></Need>} />
        <Route path="/admin/customers"     element={<Need what="see_money"><Customers /></Need>} />
        <Route path="/admin/invoices"      element={<Need what="see_money"><Invoices /></Need>} />
        <Route path="/admin/invoices/new"  element={<Need what="see_money"><InvoiceForm /></Need>} />
        <Route path="/admin/invoices/weights" element={<Need what="see_money"><WeightCalculator /></Need>} />
        <Route path="/admin/packaging" element={<Need what="see_packaging"><Packaging /></Need>} />
        <Route path="/admin/customers/:id" element={<Need what="see_money"><Customers /></Need>} />
        <Route path="/admin/bulk"          element={<Need what="manage"><BulkTool /></Need>} />
        <Route path="/admin/history"          element={<Need what="manage"><History /></Need>} />
        <Route path="/admin/history/board"    element={<Need what="manage"><HistoryBoard /></Need>} />
        <Route path="/admin/measuring"        element={<Need what="assign_measuring"><Measuring /></Need>} />
        {/* Archive became History's "No longer in use" (1 Oct 2026). */}
        <Route path="/admin/archive"          element={<Navigate to="/admin/history?view=retired" replace />} />
        <Route path="/admin/archive/jobs"     element={<Need what="manage"><JobsArchive /></Need>} />
        <Route path="/admin/archive/recipes"   element={<Need what="manage"><RecipeChanges /></Need>} />
        <Route path="/admin/archive/equipment" element={<Need what="manage"><Equipment /></Need>} />
        <Route path="/admin/users"         element={<Need what="manage"><AdminUsers /></Need>} />
        <Route path="/admin/users/:id"     element={<Need what="manage"><UserDetail /></Need>} />
        <Route path="/admin/suggestions"   element={<Need what="manage"><AdminSuggestions /></Need>} />
        <Route path="/admin/settings"      element={<Need what="manage"><Settings /></Need>} />
        <Route path="/admin/data"          element={<Need what="manage"><DataTransfer /></Need>} />
        <Route path="/account"             element={<Account />} />
        <Route path="/faq"                 element={<FAQ />} />
        <Route path="/orders"              element={<OrdersByRole />} />
        <Route path="/orders/:orderId/truck" element={<Truck />} />
        <Route path="/stock"               element={<Stock />} />
        <Route path="/stock/:id"           element={<StockItem />} />
        <Route path="/stock/build/:productId" element={<BuildSteps />} />
        <Route path="/stock/build/part/:itemId" element={<BuildSteps />} />
        <Route path="/stock/switches"      element={<Need what="manage"><StockSwitches /></Need>} />
        <Route path="/stock/dispatch/:invoiceId" element={<Need what="manage"><StockDispatch /></Need>} />
        <Route path="/admin/orders"        element={<Keep to="/orders" />} />
        <Route path="/insights"            element={<Need what="manage"><Dashboard /></Need>} />
        <Route path="/insights/all"        element={<Need what="manage"><Insights /></Need>} />
        <Route path="/financials"          element={<Need what="see_money"><Financials /></Need>} />
        {/* Legacy redirects */}
        <Route path="/machines"            element={<Navigate to="/admin/machines" />} />
        <Route path="/moulds"              element={<Navigate to="/admin/moulds" />} />
        <Route path="/materials"           element={<Navigate to="/admin/materials" />} />
        <Route path="/jobs"                element={<Navigate to="/today" />} />
        <Route path="/parameters"          element={<Navigate to="/process" />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ToastProvider>
          <Routes>
            <Route path="/*" element={<AppRoutes />} />
          </Routes>
        </ToastProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}